/**
 * Admin Setup — Client-Facing Settings
 *
 * Sidebar entry: "Admin Setup" (visible to group admins, site admins, Helix)
 * Contains settings that client administrators manage: branding, hierarchy,
 * legal, performance, quality, purchasing, onboarding, notifications, regional.
 * Organised with an internal left sidebar for section navigation.
 */
import { useState } from 'react';
import {
  Palette, FolderTree, Scale, ShieldCheck, ShoppingCart, UserPlus,
  TrendingUp, Bell, Globe, PackageCheck, LineChart, ChevronRight, Lock,
  Users, Ban, Coins, Mail,
} from 'lucide-react';
import ExchangeRatesPage from './admin/ExchangeRatesPage';
import EmailTemplatePreview from './admin/EmailTemplatePreview';
import { canViewEmailTemplates } from '../components/admin/settings/emailTemplates/useEmailTemplates';
import { canOpenAdminSetup, isCustomerSideUser, isHelixPlatformUser } from '../lib/adminSetupAccess';
import { useAuth } from '../contexts/AuthContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { useIsSupplier } from '../hooks/usePortalMode';
import PerformanceSettings from '../components/settings/PerformanceSettings';
import {
  useAdminSettings,
  BrandingTab,
  HierarchyTab,
  LegalTab,
  QualityTab,
  EscapeRootCauseFeatureSection,
  NCRNumberAndGradesSection,
  QualityTabsTab,
  PurchasingTab,
  PurchasingTabsTab,
  ForecastSettingsTab,
  OnboardingTab,
  NotificationSetupTab,
  CallOffConfigTab,
  RegionalTab,
  SecurityTab,
  SuppliersTab,
  OTIFExclusionsTab,
} from '../components/admin/settings';

type Section =
  | 'branding' | 'hierarchy' | 'regional' | 'exchange-rates' | 'legal' | 'security'
  | 'performance' | 'quality' | 'purchasing' | 'call-off'
  | 'forecast' | 'onboarding' | 'notifications' | 'email-templates' | 'suppliers'
  | 'otif-exclusions';

interface SectionDef {
  key: Section;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  group: string;
  featureFlag?: string;
  /** When set, only render when this customer dashboard tab is enabled (CR-6722). */
  dashboardTab?: string;
  /** When true, only render for Helix superusers or company admins. */
  adminOnly?: boolean;
  /**
   * CR-7259: when true, only render for Helix users and customer GROUP
   * admins. Stricter than `adminOnly`, which a site-level admin passes.
   */
  groupAdminOnly?: boolean;
  /**
   * When true, only render for Helix staff and CUSTOMER company admins —
   * never a supplier admin, never a stale is_superuser flag. Mirrors the
   * backend IsEmailTemplateViewer gate.
   */
  customerAdminOnly?: boolean;
  /**
   * CR-8217: customer configuration the API refuses to anyone but Helix and
   * customer-company admins. Hidden from logistics admins, who keep Branding,
   * Organisation, Security, Exchange Rates and Notifications for their own company.
   */
  customerSideOnly?: boolean;
  /** CR-8252: shown only while the tenant has compliance (Supplier Onboarding). */
  requiresCompliance?: boolean;
}

const SECTIONS: SectionDef[] = [
  // Company & Branding
  { key: 'branding', label: 'Branding', icon: Palette, group: 'Company' },
  { key: 'hierarchy', label: 'Organisation', icon: FolderTree, group: 'Company' },
  { key: 'regional', label: 'Regional Settings', icon: Globe, group: 'Company', customerSideOnly: true },
  // Moved here from Platform Admin (2026-09-04): company admins read their
  // own tenant's rates; the backend pins non-staff to their own company.
  { key: 'exchange-rates', label: 'Exchange Rates', icon: Coins, group: 'Company', adminOnly: true },
  { key: 'legal', label: 'Legal & Policies', icon: Scale, group: 'Company', customerSideOnly: true },
  { key: 'security', label: 'Security', icon: Lock, group: 'Company' },
  // Operations
  // CR-7259: supplier performance configuration (grade bands and their
  // colours, OTIF, PPM, the rating formula) is group-level — one scheme
  // per tenant, cascaded to every member site by CR-7079. A site-level
  // admin editing it is what made one figure render in two colours
  // depending on view scope, so the entry is group admins only. The API
  // holds the same rule, so hiding this is convenience, not the gate.
  { key: 'performance', label: 'Performance', icon: TrendingUp, group: 'Operations', groupAdminOnly: true },
  // CR-6722: OTIF exclusions only apply to schedule-order POs, so the section
  // follows the Scheduling module toggle (Scheduled dashboard tab).
  { key: 'otif-exclusions', label: 'OTIF Exclusions', icon: Ban, group: 'Operations', dashboardTab: 'scheduled', customerSideOnly: true },
  { key: 'quality', label: 'Quality', icon: ShieldCheck, group: 'Operations', customerSideOnly: true },
  { key: 'purchasing', label: 'Purchasing', icon: ShoppingCart, group: 'Operations', customerSideOnly: true },
  { key: 'call-off', label: 'Call-Off', icon: PackageCheck, group: 'Operations', featureFlag: 'switch_call_off_tab', customerSideOnly: true },
  { key: 'forecast', label: 'Forecast', icon: LineChart, group: 'Operations', customerSideOnly: true },
  { key: 'onboarding', label: 'Onboarding', icon: UserPlus, group: 'Operations', customerSideOnly: true, requiresCompliance: true },
  // Master Data (CR-5901) — gated to admins, the merge action is high-impact
  { key: 'suppliers', label: 'Suppliers', icon: Users, group: 'Master Data', adminOnly: true, customerAdminOnly: true },
  // Communications — tenant-wide delivery config (incl. the email kill
  // switch), so admins only. The API enforces the same gate.
  { key: 'notifications', label: 'Notifications', icon: Bell, group: 'Communications', adminOnly: true },
  // Every email the portal sends, previewed in the tenant's branding. View
  // only; the API returns 403 to anyone this entry is hidden from.
  { key: 'email-templates', label: 'Email Templates', icon: Mail, group: 'Communications', customerAdminOnly: true },
];

export default function AdminSetup() {
  const { user } = useAuth();
  const inSupplierView = useIsSupplier();
  // The sidebar hides the link from these users, but the URL still resolves;
  // checked before the page mounts so nothing admin-only is fetched for them.
  if (!canOpenAdminSetup(user, inSupplierView)) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You do not have access to Admin Setup.</p>
      </div>
    );
  }
  return <AdminSetupContent />;
}

function AdminSetupContent() {
  const { user } = useAuth();
  const { isFeatureEnabled, isDashboardTabEnabled, isMenuItemVisible } = useMenuSettings();
  const [activeSection, setActiveSection] = useState<Section>('branding');
  // A section that stages edits (Notification Setup) loses every one of them
  // when it unmounts, and switching section unmounts it. Guarded here rather
  // than inside the section, because the section never learns it is leaving.
  const unsaved = useUnsavedChangesGuard();

  // Mirrors backend IsHelixOrCompanyAdmin: superuser, Helix platform user,
  // or a company admin (any role flagged is_admin_role on their profile).
  const isAdmin = Boolean(
    user?.is_superuser || user?.company_type === 'helix' || user?.is_company_admin,
  );

  // CR-7259. Mirrors backend is_customer_group_admin(): Helix platform users,
  // or a customer admin whose held sites cover the whole group. Read straight
  // off the user payload rather than re-derived here — the group membership
  // this depends on is not on the client.
  const isGroupAdmin = Boolean(
    user?.company_type === 'helix' || user?.is_group_admin,
  );

  // Email Templates: Helix staff or a customer admin — supplier admins are
  // excluded even though isAdmin admits them.
  const isCustomerAdmin = canViewEmailTemplates(user);
  const isCustomerSide = isCustomerSideUser(user);
  // CR-8238: tab visibility is platform-wide; only Helix saves it.
  const isHelix = isHelixPlatformUser(user);

  const {
    themeSettings, setThemeSettings,
    platformSettings, setPlatformSettings,
    barrattFlags, setBarrattFlags,
    purchasingTabs, setPurchasingTabs, savePurchasingTabs,
    qualityTabs, setQualityTabs, saveQualityTabs,
    siteSettings, choices, loadingSites, savingSite, editedSites,
    groupDefaults, loadingGroupDefaults, togglingInheritance, siteSettingsError, savingGroupDefaults,
    saving,
    saveThemeSettings, savePlatformSettings,
    handleFileUpload, uploadingFiles, brandingImages, resetColorsToDefault,
    handleSiteSettingChange, saveSiteSettings, setSiteInheritance, saveGroupDefaults,
  } = useAdminSettings();

  const visibleSections = SECTIONS.filter(s => {
    if (s.featureFlag && !isFeatureEnabled(s.featureFlag as Parameters<typeof isFeatureEnabled>[0])) return false;
    if (s.dashboardTab && !isDashboardTabEnabled(s.dashboardTab)) return false;
    if (s.adminOnly && !isAdmin) return false;
    if (s.groupAdminOnly && !isGroupAdmin) return false;
    if (s.customerAdminOnly && !isCustomerAdmin) return false;
    if (s.customerSideOnly && !isCustomerSide) return false;
    if (s.requiresCompliance && !isMenuItemVisible('onboarding')) return false;
    return true;
  });

  // CR-6722: if the active section becomes hidden (e.g. the Scheduling module
  // is disabled while OTIF Exclusions is open), fall back to the first visible
  // section so a hidden section's content can never render.
  const currentSection: Section = visibleSections.some(s => s.key === activeSection)
    ? activeSection
    : (visibleSections[0]?.key ?? 'branding');

  const groups = Array.from(new Set(visibleSections.map(s => s.group)));

  /** Switch section, asking first when the open one is holding unsaved work. */
  const selectSection = (key: Section) => {
    if (key === currentSection) return;
    if (!unsaved.confirmDiscard()) return;
    setActiveSection(key);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Admin Setup</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Company settings, module configuration, and policies
        </p>
      </div>

      <div className="flex gap-6">
        {/* Left sidebar with grouped sections — sticky so it stays while scrolling */}
        <nav className="w-52 shrink-0 space-y-4 sticky top-6 self-start max-h-[calc(100vh-8rem)] overflow-y-auto">
          {groups.map(group => (
            <div key={group}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-1">{group}</p>
              <div className="space-y-0.5">
                {visibleSections.filter(s => s.group === group).map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => selectSection(key)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                      currentSection === key
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{label}</span>
                    {currentSection === key && <ChevronRight className="h-3 w-3 ml-auto" />}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Content area */}
        <div className="flex-1 min-w-0">
          {currentSection === 'branding' && (
            <BrandingTab
              themeSettings={themeSettings} saving={saving}
              onThemeSettingsChange={setThemeSettings} onSave={saveThemeSettings}
              onResetColors={resetColorsToDefault} onFileUpload={handleFileUpload}
              // CR-6243: feeds the upload tiles' previews and spinners.
              brandingImages={brandingImages} uploadingFiles={uploadingFiles}
            />
          )}
          {currentSection === 'hierarchy' && <HierarchyTab />}
          {currentSection === 'regional' && (
            <RegionalTab
              siteSettings={siteSettings} choices={choices}
              groupDefaults={groupDefaults} loadingGroupDefaults={loadingGroupDefaults}
              siteSettingsError={siteSettingsError}
              loadingSites={loadingSites} savingSite={savingSite}
              togglingInheritance={togglingInheritance} savingGroupDefaults={savingGroupDefaults}
              editedSites={editedSites}
              onSiteSettingChange={handleSiteSettingChange}
              onSaveSiteSettings={saveSiteSettings}
              onSiteInheritanceChange={setSiteInheritance}
              onSaveGroupDefaults={saveGroupDefaults}
            />
          )}
          {currentSection === 'exchange-rates' && <ExchangeRatesPage embedded />}
          {currentSection === 'legal' && <LegalTab />}
          {currentSection === 'security' && <SecurityTab />}
          {currentSection === 'performance' && <PerformanceSettings />}
          {currentSection === 'otif-exclusions' && <OTIFExclusionsTab />}
          {currentSection === 'quality' && (
            <div className="space-y-6">
              <QualityTab />
              <EscapeRootCauseFeatureSection />
              <NCRNumberAndGradesSection />
              {isHelix && <QualityTabsTab qualityTabs={qualityTabs} saving={saving} onQualityTabsChange={setQualityTabs} onSave={saveQualityTabs} />}
            </div>
          )}
          {currentSection === 'purchasing' && (
            <div className="space-y-6">
              <PurchasingTab />
              {isHelix && <PurchasingTabsTab purchasingTabs={purchasingTabs} saving={saving} onPurchasingTabsChange={setPurchasingTabs} onSave={savePurchasingTabs} />}
            </div>
          )}
          {currentSection === 'call-off' && <CallOffConfigTab />}
          {currentSection === 'forecast' && (
            <ForecastSettingsTab barrattFlags={barrattFlags} saving={saving} onBarrattFlagsChange={setBarrattFlags} onSave={savePlatformSettings} />
          )}
          {currentSection === 'onboarding' && (
            <OnboardingTab />
          )}
          {currentSection === 'suppliers' && <SuppliersTab />}
          {currentSection === 'notifications' && (
            <NotificationSetupTab
              platformSettings={platformSettings} saving={saving}
              onPlatformSettingsChange={setPlatformSettings} onSave={savePlatformSettings}
              onUnsavedChangesChange={unsaved.setUnsavedCount}
            />
          )}
          {currentSection === 'email-templates' && <EmailTemplatePreview embedded />}
        </div>
      </div>
    </div>
  );
}
